import { GenericActionResponse } from "../@types/index";
import { BasesCalculoApiResponse, BasesCalculoEdicaoRequest, DescricoesVerbasApiResponse, ImpostosApiResponse, ImpostosEdicaoRequest, IncidenciasVerbasApiResponse, VerbasApiResponse } from "../@types/esocial";
import { Token } from "../utils/localStorage.util";
import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import config from "../config.js";

const apiEsocial = axios.create({
  baseURL: config.REACT_APP_HOST_API_ESOCIAL,
});

apiEsocial.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = Token.getToken();
    if (token) {
      config.headers.Authorization = token;
    }

    if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json";
    }

    return config;
  },
  (error: AxiosError) => {
    Promise.reject(error);
  }
);

apiEsocial.interceptors.response.use(
  (response: AxiosResponse) => {
    return response.data;
  },

  async function (error: AxiosError) {
    if (error?.code === "ERR_NETWORK") {
      return;
    }

    return Promise.reject(error);
  }
);

export default apiEsocial;

export function obterBasesCalculo(idEmpresa: number): Promise<BasesCalculoApiResponse> {
  return apiEsocial.get(`/bases-calculo/${idEmpresa}`);
}

export function obterBasesCalculoPorCampoDeBusca(idEmpresa: number, textoBusca: string): Promise<BasesCalculoApiResponse> {
  return apiEsocial.get(`/bases-calculo/${idEmpresa}/buscar-por-campos?textoDeBusca=${encodeURIComponent(textoBusca)}`);
}

export function cadastrarBasesCalculo(idEmpresa: number, baseCalculo: BasesCalculoEdicaoRequest): Promise<GenericActionResponse> {
  return apiEsocial.post(`/bases-calculo/${idEmpresa}`, baseCalculo);
}

export function atualizarBasesCalculo(idEmpresa: number, idBase: number, baseCalculo: BasesCalculoEdicaoRequest): Promise<GenericActionResponse> {
  return apiEsocial.put(`/bases-calculo/${idEmpresa}/${idBase}`, baseCalculo);
}

export function excluirBasesCalculo(idEmpresa: number, idBase: number): Promise<GenericActionResponse> {
  return apiEsocial.delete(`/bases-calculo/${idEmpresa}/${idBase}`);
}

export function obterImpostos(idEmpresa: number): Promise<ImpostosApiResponse> {
  return apiEsocial.get(`/impostos/${idEmpresa}`);
}

export function obterImpostosPorCampoDeBusca(idEmpresa: number, textoBusca: string): Promise<ImpostosApiResponse> {
  return apiEsocial.get(`/impostos/${idEmpresa}/buscar-por-campos?textoDeBusca=${encodeURIComponent(textoBusca)}`);
}

export function cadastrarImpostos(idEmpresa: number, impostos: ImpostosEdicaoRequest): Promise<GenericActionResponse> {
  return apiEsocial.post(`/impostos/${idEmpresa}`, impostos);
}

export function atualizarImpostos(idEmpresa: number, idImposto: number, impostos: ImpostosEdicaoRequest): Promise<GenericActionResponse> {
  return apiEsocial.put(`/impostos/${idEmpresa}/${idImposto}`, impostos);
}

export function excluirImpostos(idEmpresa: number, idImposto: number): Promise<GenericActionResponse> {
  return apiEsocial.delete(`/impostos/${idEmpresa}/${idImposto}`);
}

export function obterVerbas(idEmpresa: number): Promise<VerbasApiResponse> {
  return apiEsocial.get(`/verbas/${idEmpresa}`);
}

export function obterVerbasPorCampoDeBusca(idEmpresa: number, textoBusca: string): Promise<VerbasApiResponse> {
  return apiEsocial.get(`/verbas/${idEmpresa}/buscar-por-campos?textoDeBusca=${encodeURIComponent(textoBusca)}`);
}

export function obterVerbasAjustadas(idEmpresa: number): Promise<VerbasApiResponse> {
  return apiEsocial.get(`/verbas/${idEmpresa}/ajustes`);
}

export function obterVerbasAjustadasPorCampoDeBusca(idEmpresa: number, textoBusca: string): Promise<VerbasApiResponse> {
  return apiEsocial.get(`/verbas/${idEmpresa}/ajustes/buscar-por-campos?textoDeBusca=${encodeURIComponent(textoBusca)}`);
}

export function obterFolhas(idEmpresa: number, mesAno?: string) {
  let url = `/folha/${idEmpresa}`
  if (mesAno) url += `?perapur=${encodeURIComponent(mesAno)}`;
  return apiEsocial.get(url);
}

export function obterFolhasCompleto(idEmpresa: number) {
  return apiEsocial.get(`/folha/${idEmpresa}/completo`);
}

export function obterFolhasPorTexto(idEmpresa: number, textoBusca: string, mesAno?: string) {
  let url = `/folha/${idEmpresa}/buscar-folha?textoDeBusca=${encodeURIComponent(textoBusca)}`
  if (mesAno) url += `&perapur=${mesAno}`;
  return apiEsocial.get(url);
}

export function obterFolhasManuais(idEmpresa: number, mesAno: string) {
  return apiEsocial.get(`/folha/${idEmpresa}/manual?perapur=${encodeURIComponent(mesAno)}`);
}

export function obterFolhasManuaisPorTexto(idEmpresa: number, mesAno: string, textoBusca: string) {
  return apiEsocial.get(`/folha/${idEmpresa}/manual/buscar-folha?perapur=${encodeURIComponent(mesAno)}&textoDeBusca=${encodeURIComponent(textoBusca)}`);
}

export function obterSomatoriasFolhasManuais(idEmpresa: number, mesAno: string) {
  return apiEsocial.get(`/folha/${idEmpresa}/somatorias?perapur=${encodeURIComponent(mesAno)}`);
}

export function ajustarSomatoriasFolhaManual(idEmpresa: number, somatoria: any) {
  return apiEsocial.put(`/folha/${idEmpresa}/somatorias`, somatoria);
}

export function obterResumoGeralFolha(body: { cnpjs: number[] }) {
  return apiEsocial.post(`/folha/resumo-geral`, body);
}

export function obterResumoGeralFolhaFiltrado(empresas: number[], codigos: string[]) {
  return apiEsocial.post(`/folha/resumo-geral`, { ...empresas, codigos });
}

export function editarFolhaManual(idEmpresa: number, folhaManual: string) {
  return apiEsocial.post(`/folha/${idEmpresa}/manual`, folhaManual);
}

export function obterVerbasManuais(idEmpresa: number) {
  return apiEsocial.get(`/verbas/${idEmpresa}/manual`);
}

export function obterVerbasManuaisPorTexto(idEmpresa: number, textoDeBusca: string) {
  return apiEsocial.get(`/verbas/${idEmpresa}/manual/buscar-verba?textoDeBusca=${encodeURIComponent(textoDeBusca)}`);
}

export function criarVerbaManual(idEmpresa: number, dados: any) {
  return apiEsocial.post(`/verbas/${idEmpresa}/manual`, dados);
}

export function criarAjusteManualVerba(idEmpresa: number, dados: any) {
  return apiEsocial.post(`/verbas/${idEmpresa}/ajuste`, dados);
}

export function atualizarVerbaManual(idEmpresa: number, idVerba: number, dados: any) {
  return apiEsocial.put(`/verbas/${idEmpresa}/manual/${idVerba}`, dados);
}

export function deletarVerbaManual(idEmpresa: number, idVerba: number) {
  return apiEsocial.delete(`/verbas/${idEmpresa}/manual/${idVerba}`);
}

export function obterIncidenciaVerbasManuais(idEmpresa: number, idVerba: number) {
  return apiEsocial.get(`/verbas/${idEmpresa}/manual/${idVerba}/incidencias`);
}

export function atualizarIncidenciaVerbasManuais(idEmpresa: number, idVerba: number, dados: any) {
  return apiEsocial.put(`/verbas/${idEmpresa}/manual/${idVerba}/incidencias`, dados);
}